import { Link } from "react-router-dom";
import { LogoMark } from "@/components/Logo";
import EstimateTable from "@/components/EstimateTable";
import { DATE_LONG, PORTAL_NAME } from "@/data/site";

export default function Letter() {
  return (
    <div className="min-h-screen bg-bone">
      <div className="no-print max-w-4xl mx-auto px-6 py-6 flex items-center justify-between sticky top-0 bg-bone/90 backdrop-blur z-50 border-b border-linec">
        <Link to="/" data-testid="letter-back-link" className="font-mono text-[10px] tracking-[0.2em] uppercase text-dim hover:text-ember transition-colors">&larr; Back to proposal</Link>
        <button onClick={() => window.print()} data-testid="print-proposal-btn"
          className="bg-ember text-white font-body font-semibold text-xs tracking-wide px-6 py-3 rounded-sm hover:bg-glow transition-colors">
          Print / Save as PDF
        </button>
      </div>

      <div className="print-page max-w-4xl mx-auto my-10 px-8 md:px-16 py-14 shadow-xl border border-linec" data-testid="letter-document">
        {/* letterhead */}
        <div className="flex items-start justify-between border-b-2 border-navy pb-8">
          <div className="flex items-center gap-3">
            <LogoMark size={44} />
            <div>
              <p className="font-body font-bold text-lg text-navy">Backer Marathon</p>
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-navy/50 mt-1">Website Redesign Proposal</p>
            </div>
          </div>
          <p className="font-mono text-xs text-navy/50 text-right">{DATE_LONG}</p>
        </div>

        <p className="font-mono text-xs text-navy/60 mt-8">To: Bob<br />Re: Backer Marathon website redesign — proposal &amp; estimate</p>

        <h1 className="font-body font-semibold text-3xl text-navy mt-10 leading-tight">
          Dear Bob,
        </h1>

        <div className="text-navy/80 leading-relaxed space-y-5 mt-6 text-[15px]">
          <p>
            Thank you for the full picture from your Marathon meeting. Between the locked CMS, the ten-year-old IMDB
            server and the blocked HubSpot integration, this is very fixable — and none of it requires keeping the
            IMDB box on life support. Here is our thinking and the numbers.
          </p>
          <p className="font-semibold text-navy">1. The IMDB server — our honest recommendation: don't replace it, replace it.</p>
          <p>
            You asked whether IMDB is even necessary. As a server, no. It exists only because the public website can't
            search or quote. Rather than the "new virtual instance + connectivity project" path, we extract its product
            and pricing data once and rebuild it as a public Product Search + Instant RFQ portal on the new site — you
            can try a working prototype here: the {PORTAL_NAME} demo in the proposal we sent. Marathon customers
            research and quote in seconds; pricing lives in one database your team owns; the old server is decommissioned,
            along with its maintenance contract.
          </p>
          <p className="font-semibold text-navy">2. CMS — own it outright.</p>
          <p>
            The rebuild runs on Strapi — a proven, open-source headless CMS, hosted on Strapi Cloud's Growth plan
            (~$540/year, billed separately) so Marathon's team manages all content with zero server worries —
            updates, backups and uptime are handled. The Growth plan also includes Strapi's AI service (1,000
            credits a month): AI-assisted content modeling, one-click translation and automatic media tagging —
            so product descriptions can be drafted straight from spec sheets, hundreds of heater photos become a
            tagged, searchable library, and multilingual pages are a click away. Full admin access from day one,
            plus training and handover documentation. "Headless" means the CMS stores and manages all content
            while the website is built separately on a modern front end — one content source feeds the website,
            the customer portal and any future application. No more developer gatekeeping for cosmetic changes.
            Custom code lives only where it must: the product catalog, pricing tiers and wizard logic, with a
            purpose-built admin the team also owns. And the platform is AI-native: Strapi's new MCP server (GA,
            included free) lets approved AI assistants securely read, update and publish content in plain
            language — permissions scoped per user, nothing exposed unless switched on.
          </p>
          <p className="font-semibold text-navy">3. HubSpot — wired in properly.</p>
          <p>
            Every RFQ and form becomes a HubSpot contact and deal automatically, with lead routing and closed-loop
            reporting. The integration is part of the build, not an afterthought.
          </p>
        </div>

        <div className="mt-10">
          <h2 className="font-body font-semibold text-xl text-navy">The estimate</h2>
          <p className="text-navy/60 text-sm mt-2">Quoted as three fixed-price modules — one engagement, everything included; the current IMDB runs untouched until cutover.</p>
          <div className="mt-6"><EstimateTable light /></div>
        </div>

        <div className="text-navy/80 leading-relaxed space-y-5 mt-10 text-[15px]">
          <p className="font-semibold text-navy">What we need from Marathon to start:</p>
          <p>
            A read-only export of the IMDB product and pricing tables, HubSpot admin access, and the ERP API
            documentation (with sandbox/test credentials) from the current developer. That's the whole list — we'd
            suggest requesting both this week so nothing delays kickoff.
          </p>
          <p>
            We'd love to join next week's management meeting and walk the room through the live demo — fifteen minutes
            covers the whole story: old box out, owned system in, pipeline heating up.
          </p>
        </div>

        <div className="mt-12 border-t border-linec pt-8">
          <p className="text-navy">Best regards,</p>
          <p className="font-body font-semibold text-xl text-navy mt-4">The Team</p>
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-navy/50 mt-1">
            Backer Marathon · Website Redesign Proposal · Valid 60 days
          </p>
        </div>
      </div>
    </div>
  );
}
